// Clinical analysis utilities - aggregate care programs by patient and week
// for trend visualization. All computations are pure functions over the
// programs payload returned by /api/programs.

import { computePriority } from "@/lib/priority";
import { computeWaterBalance } from "@/lib/balance";
import { computeMorseTotal, isMorseFilled } from "@/lib/morse";

function num(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function parseTA(v) {
  if (!v) return { sys: null, dia: null };
  const m = String(v).trim().match(/^(\d{2,3})\s*\/\s*(\d{2,3})$/);
  if (!m) return { sys: null, dia: null };
  return { sys: parseInt(m[1], 10), dia: parseInt(m[2], 10) };
}

function avg(xs) {
  const valid = xs.filter((x) => Number.isFinite(x));
  if (valid.length === 0) return null;
  return valid.reduce((s, x) => s + x, 0) / valid.length;
}

// Pick the start-date of the shift (sortable YYYY-MM-DD).
function shiftDate(p) {
  return p?.shift?.date || "";
}

// ISO week id: "YYYY-Www" (e.g., "2026-W08"). Returns "" if invalid.
export function isoWeekId(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  // Thursday in current week decides the year (ISO 8601).
  const target = new Date(d);
  target.setUTCDate(target.getUTCDate() + 3 - ((target.getUTCDay() + 6) % 7));
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const weekNo =
    1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${target.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

// Monday of the ISO week that contains dateStr (YYYY-MM-DD), as YYYY-MM-DD.
export function isoWeekStart(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  const day = (d.getUTCDay() + 6) % 7; // 0 = Monday
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
}

// Distinct patient names across the dataset with their program count and date range.
export function listPatients(programs) {
  const map = new Map();
  for (const p of programs || []) {
    const name = (p?.shift?.patient_name || "").trim();
    if (!name) continue;
    const date = shiftDate(p);
    if (!map.has(name)) {
      map.set(name, { name, count: 0, first: date, last: date });
    }
    const entry = map.get(name);
    entry.count += 1;
    if (date && (!entry.first || date < entry.first)) entry.first = date;
    if (date && (!entry.last || date > entry.last)) entry.last = date;
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

// Build a per-shift data point with all metrics required by the charts.
// Returns { date, week, weekStart, ownerName, priority, evaPain, ta_sys, ta_dia,
//           fc, temp, spo2, waterBalance, intake, output, morseTotal }
export function programToPoint(program) {
  const s = program?.shift || {};
  const c = program?.closing || {};
  const v = c.vitals || {};
  const readings = Array.isArray(v.readings) ? v.readings : [];

  // Average across the 3 readings (skip empty ones)
  const taSys = [];
  const taDia = [];
  const fc = [];
  const temp = [];
  const spo2 = [];
  for (const r of readings) {
    if (!r) continue;
    const ta = parseTA(r.ta);
    if (ta.sys != null) taSys.push(ta.sys);
    if (ta.dia != null) taDia.push(ta.dia);
    const f = num(r.fc);
    if (f != null) fc.push(f);
    const t = num(r.temp);
    if (t != null) temp.push(t);
    const sp = num(r.spo2);
    if (sp != null) spo2.push(sp);
  }

  // Water balance
  const wb = computeWaterBalance(c, s);

  // Morse
  const morseFilled = isMorseFilled(c.morse);
  const morseTotal = morseFilled ? computeMorseTotal(c.morse) : null;

  // Sleep (count of hour-buttons marked as slept; 0-24)
  const sleepArr = Array.isArray(c.sleep?.hours) ? c.sleep.hours : [];
  const sleepHasObs = !!(c.sleep?.observations || "").trim();
  const sleepHours = sleepArr.length > 0 || sleepHasObs ? sleepArr.length : null;

  // Priority
  const prio = computePriority(program);

  const date = shiftDate(program);
  return {
    programId: program.id,
    date,
    week: isoWeekId(date),
    weekStart: isoWeekStart(date),
    ownerName: program.owner_name || "",
    priority: prio.level,
    priorityReasons: prio.reasons || [],
    evaPain: Number.isFinite(c.eva_pain) ? c.eva_pain : null,
    ta_sys: avg(taSys),
    ta_dia: avg(taDia),
    fc: avg(fc),
    temp: avg(temp),
    spo2: avg(spo2),
    waterBalance: wb ? wb.balance : null,
    intake: wb ? wb.intake : null,
    output: wb ? wb.output : null,
    morseTotal,
    sleepHours,
  };
}

// All shifts for a patient sorted by date ascending, optionally filtered by
// date range (inclusive, YYYY-MM-DD strings).
export function patientPoints(programs, patientName, fromDate, toDate) {
  const name = (patientName || "").trim();
  if (!name) return [];
  const points = (programs || [])
    .filter((p) => (p?.shift?.patient_name || "").trim() === name)
    .filter((p) => {
      const d = shiftDate(p);
      if (!d) return false;
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    })
    .map(programToPoint)
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  return points;
}

// Aggregate per-shift points into one entry per ISO week.
// Output: [{ week, weekStart, label, shiftsCount, priorityCounts, evaPain,
//            ta_sys, ta_dia, fc, temp, spo2, waterBalance, intake, output, morseTotal }]
export function aggregateByWeek(points) {
  const buckets = new Map();
  for (const p of points) {
    if (!p.week) continue;
    if (!buckets.has(p.week)) {
      buckets.set(p.week, {
        week: p.week,
        weekStart: p.weekStart,
        shifts: [],
      });
    }
    buckets.get(p.week).shifts.push(p);
  }
  return Array.from(buckets.values())
    .sort((a, b) => (a.weekStart || "").localeCompare(b.weekStart || ""))
    .map((b) => {
      const priorityCounts = { red: 0, yellow: 0, green: 0, gray: 0 };
      for (const s of b.shifts) priorityCounts[s.priority] = (priorityCounts[s.priority] || 0) + 1;
      const pick = (k) => b.shifts.map((s) => s[k]).filter((x) => Number.isFinite(x));
      const a = (xs) => (xs.length === 0 ? null : xs.reduce((s, x) => s + x, 0) / xs.length);
      return {
        week: b.week,
        weekStart: b.weekStart,
        label: shortWeekLabel(b.week, b.weekStart),
        shiftsCount: b.shifts.length,
        priorityCounts,
        evaPain: a(pick("evaPain")),
        ta_sys: a(pick("ta_sys")),
        ta_dia: a(pick("ta_dia")),
        fc: a(pick("fc")),
        temp: a(pick("temp")),
        spo2: a(pick("spo2")),
        waterBalance: a(pick("waterBalance")),
        intake: a(pick("intake")),
        output: a(pick("output")),
        morseTotal: a(pick("morseTotal")),
        sleepHours: a(pick("sleepHours")),
      };
    });
}

// Short label like "Sem 23\n08 jun" for chart X axis.
function shortWeekLabel(weekId, weekStart) {
  const m = weekId.match(/W(\d{2})$/);
  const w = m ? `Sem ${parseInt(m[1], 10)}` : weekId;
  if (!weekStart) return w;
  const d = new Date(`${weekStart}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return w;
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${w} (${day} ${months[d.getUTCMonth()]})`;
}

// Summary stats for the selected date range.
export function summarize(points) {
  if (!points.length) {
    return {
      totalShifts: 0,
      nurses: [],
      priorityCounts: { red: 0, yellow: 0, green: 0, gray: 0 },
      firstDate: "",
      lastDate: "",
    };
  }
  const priorityCounts = { red: 0, yellow: 0, green: 0, gray: 0 };
  const nurseSet = new Set();
  for (const p of points) {
    priorityCounts[p.priority] = (priorityCounts[p.priority] || 0) + 1;
    if (p.ownerName) nurseSet.add(p.ownerName);
  }
  return {
    totalShifts: points.length,
    nurses: Array.from(nurseSet).sort(),
    priorityCounts,
    firstDate: points[0].date,
    lastDate: points[points.length - 1].date,
  };
}

// Friendly date label for daily X axis.
export function dayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return dateStr;
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${months[d.getUTCMonth()]}`;
}

// Aggregate material consumption for a patient within a date range.
// Output sorted by quantity desc: [{ name, unit, quantity, shifts }]
export function materialsSummary(programs, patientName, fromDate, toDate) {
  const name = (patientName || "").trim();
  if (!name) return [];
  const map = new Map();
  for (const p of programs || []) {
    if ((p?.shift?.patient_name || "").trim() !== name) continue;
    const d = shiftDate(p);
    if (!d) continue;
    if (fromDate && d < fromDate) continue;
    if (toDate && d > toDate) continue;
    for (const m of p.materials || []) {
      const label = (m?.name || "").trim();
      if (!label) continue;
      const unit = (m?.unit || "pz").trim();
      const key = `${label.toLowerCase()}|${unit.toLowerCase()}`;
      const cur = map.get(key) || { name: label, unit, quantity: 0, shifts: 0 };
      cur.quantity += Number(m.quantity) || 0;
      cur.shifts += 1;
      map.set(key, cur);
    }
  }
  return [...map.values()].sort(
    (a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name)
  );
}
